import {
  Box,
  Button,
  FormHelperText,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useRouter } from "next/router";
import type { FC } from "react";
import { useAuth } from "../../hooks/use-auth";
import { useMounted } from "../../hooks/use-mounted";
import { validatePassword } from "./helper";
import { PasswordRules } from "./password-rules";
import { registerValidationSchema } from "./schema";

export const AmplifyRegister: FC = (props) => {
  const isMounted = useMounted();
  const router = useRouter();
  const { register } = useAuth() as any;

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      submit: null,
    },
    validate: (values) => validatePassword(values, registerValidationSchema),
    onSubmit: async (values, helpers): Promise<void> => {
      try {
        const email = values.email.toLowerCase();
        await register(email, values.password);

        sessionStorage.setItem("username", email);

        if (isMounted()) {
          // Check if returnUrl exists in the URL query parameters
          const returnUrl = router.query.returnUrl as string;

          if (returnUrl && returnUrl !== "null" && returnUrl !== "undefined") {
            // Redirect to verification with returnUrl
            router.push(`/authentication/verify-code?returnUrl=${returnUrl}`);
          } else {
            // Standard redirect if no returnUrl
            router.push("/authentication/verify-code");
          }
        }
      } catch (err) {
        console.error(err);

        if (isMounted()) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err.message });
          helpers.setSubmitting(false);
        }
      }
    },
  });

  return (
    <form noValidate onSubmit={formik.handleSubmit} {...props}>
      <TextField
        error={Boolean(formik.touched.email && formik.errors.email)}
        fullWidth
        helperText={formik.touched.email && formik.errors.email}
        label="Email Address"
        margin="normal"
        name="email"
        onBlur={formik.handleBlur}
        onChange={formik.handleChange}
        type="email"
        value={formik.values.email}
      />
      <TextField
        error={Boolean(formik.touched.password && formik.errors.password)}
        fullWidth
        label="Password"
        margin="normal"
        name="password"
        onBlur={formik.handleBlur}
        onChange={formik.handleChange}
        type="password"
        value={formik.values.password}
      />
      <PasswordRules formik={formik} />
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          mt: 2,
        }}
      >
        <Typography color="textSecondary" variant="body2">
          By signing up I accept the{" "}
          <Link component="a" href="https://seowind.io/terms" target="_blank">
            Terms and Conditions
          </Link>{" "}
          and{" "}
          <Link
            component="a"
            href="https://seowind.io/privacy-policy/"
            target="_blank"
          >
            Privacy Policy
          </Link>
          .
        </Typography>
      </Box>
      {formik.errors.submit && (
        <Box sx={{ mt: 3 }}>
          <FormHelperText error>{formik.errors.submit}</FormHelperText>
        </Box>
      )}
      <Box sx={{ mt: 2 }}>
        <Button
          disabled={formik.isSubmitting}
          fullWidth
          size="large"
          type="submit"
          variant="contained"
        >
          Sign up
        </Button>
      </Box>
    </form>
  );
};
